// Unpublished Work © 2026 Deere & Company.

import dayjs from 'dayjs';
import moment from 'moment';
import {WORKBOARD} from 'OnLabor/workboard/constants/event-types';
import {getWorkboardTemplatesByMembershipId} from 'Services/membership-service';
import {getTimeFromDate} from 'Utils/time-utils';
import {normalizeName} from './workboard-conflict-utils';

const WORKBOARD_ID_KEY = 'workboardId';
const TEMPLATE_ID_KEY = 'workboardTemplateWorkboardId';
const WORKBOARD_DATE_KEY_FORMAT = 'YYYY-MM-DD';
const WORKBOARD_DATE_SELECT_FORMAT = 'YYYYMMDD';

function normalizeEntityId(entityId) {
    if (entityId === null || entityId === undefined) {
        return null;
    }

    return String(entityId).trim().toLowerCase();
}

function getEntityId(item, idKey) {
    if (!item || !idKey) {
        return null;
    }

    return item?.[idKey];
}

function hasDuplicateForEntity(existingIds, isEditing, currentEntityId) {
    if (!existingIds || existingIds.size === 0) {
        return false;
    }

    if (!isEditing) {
        return true;
    }

    if (!currentEntityId) {
        return false;
    }

    const currentId = normalizeEntityId(currentEntityId);

    if (!currentId) {
        return false;
    }

    if (!existingIds.has(currentId)) {
        return true;
    }

    return existingIds.size > 1;
}

function createKeyToIdsMap(items, idKey, keySelector) {
    return items.reduce((acc, item) => {
        const key = keySelector(item);
        const entityId = normalizeEntityId(getEntityId(item, idKey));

        if (!key || !entityId) {
            return acc;
        }

        const existingIds = acc.get(key) || new Set();

        existingIds.add(entityId);
        acc.set(key, existingIds);

        return acc;
    }, new Map());
}

function removeEntityIdFromKeyToIdsMap(existingIdsMap = new Map(), entityId) {
    const normalizedEntityId = normalizeEntityId(entityId);

    if (!normalizedEntityId) {
        return existingIdsMap;
    }

    return new Map(
        [...existingIdsMap.entries()].reduce((acc, [key, ids]) => {
            if (!ids || ids.size === 0) {
                return acc;
            }

            if (!ids.has(normalizedEntityId)) {
                acc.push([key, ids]);

                return acc;
            }

            const nextIds = new Set(ids);

            nextIds.delete(normalizedEntityId);

            if (nextIds.size > 0) {
                acc.push([key, nextIds]);
            }

            return acc;
        }, [])
    );
}

function getWorkboardDateTimeKey(dateTime) {
    if (!dateTime) {
        return null;
    }

    const normalizedDateTime = moment.isMoment(dateTime) ? dayjs(dateTime.toDate()) : dayjs(dateTime);

    if (!normalizedDateTime.isValid()) {
        return null;
    }

    return `${normalizedDateTime.format(WORKBOARD_DATE_KEY_FORMAT)} ${getTimeFromDate(normalizedDateTime)}`;
}

function getWorkboardDateTimeForDetails(workboardData = {}) {
    const {
        dateSelect, startTime
    } = workboardData;

    if (!startTime) {
        return startTime;
    }

    if (!dateSelect) {
        return startTime;
    }

    const parsedDate = moment(dateSelect, WORKBOARD_DATE_SELECT_FORMAT, true);
    const parsedStartTime = moment(startTime, 'YYYY-MM-DD HH:mm:ss', true);

    if (!parsedDate.isValid() || !parsedStartTime.isValid()) {
        return startTime;
    }

    return parsedDate
        .set({
            hour: parsedStartTime.hour(),
            minute: parsedStartTime.minute(),
            second: parsedStartTime.second(),
            millisecond: 0
        })
        .format('YYYY-MM-DD HH:mm:ss');
}

function createNamesToIdsMap(items = [], idKey = '') {
    return createKeyToIdsMap(items, idKey, (item) => normalizeName(item?.name));
}

function isDuplicateNameForEntity({
    normalizedName,
    existingNameIdsMap,
    isEditing,
    currentEntityId
}) {
    if (!normalizedName) {
        return false;
    }

    const existingIds = existingNameIdsMap.get(normalizedName);

    return hasDuplicateForEntity(existingIds, isEditing, currentEntityId);
}

async function getWorkboardDataForNameValidation({
    isTemplate,
    membershipId,
    signal
}) {
    if (isTemplate) {
        const {workboardTemplateWorkboards = [] } = await getWorkboardTemplatesByMembershipId(membershipId, signal);

        return {
            namesData: workboardTemplateWorkboards,
            idKey: TEMPLATE_ID_KEY
        };
    }

    return {
        namesData: [],
        idKey: WORKBOARD_ID_KEY
    };
}

function formatWorkboards(workboardData = []) {
    const sortedWorkboards = [...workboardData].sort((workboard1, workboard2) => {
        if (workboard1.startTime && workboard2.startTime) {
            return moment(workboard1.startTime).diff(workboard2.startTime);
        }

        return moment(workboard1.endTime).diff(workboard2.endTime);
    });

    return sortedWorkboards.map((workboard) => ({
        ...workboard,
        end: workboard.endTime,
        id: workboard.workboardId,
        sortIndex: 1,
        start: workboard.startTime,
        title: workboard.name,
        type: WORKBOARD
    }));
}

export {
    createNamesToIdsMap,
    removeEntityIdFromKeyToIdsMap,
    isDuplicateNameForEntity,
    getWorkboardDataForNameValidation,
    formatWorkboards,
    getWorkboardDateTimeForDetails,
    getWorkboardDateTimeKey
};
